/**
 * Country hover and click interaction for the flat projection view.
 * - Hover: draws a white outline around the country under the cursor
 * - Click: shows a small floating modal with the country name + Wikipedia link
 *
 * Lifecycle is managed by MapProjectionView (activate → init, deactivate → cleanup).
 */

import { emit } from '../core/event-bus.js';
import { isUIEvent } from './interaction-guard.js';

function pointInPolygon(px, py, coords) {
    let inside = false;
    const n = coords.length / 2;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = coords[i * 2],     yi = coords[i * 2 + 1];
        const xj = coords[j * 2],     yj = coords[j * 2 + 1];
        if (((yi > py) !== (yj > py)) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
}

function findCountryAtPoint(localX, localY, hitTestCache) {
    for (const [countryId, polygons] of hitTestCache) {
        for (const coords of polygons) {
            if (pointInPolygon(localX, localY, coords)) return countryId;
        }
    }
    return null;
}

function createModal() {
    const el = document.createElement('div');
    el.id = 'country-modal';
    el.addEventListener('mouseleave', hideModal);
    document.body.appendChild(el);
    return el;
}

function showModal(country, clientX, clientY) {
    const modal = document.getElementById('country-modal') || createModal();
    const wikiSlug = encodeURIComponent(country.name.replace(/ /g, '_'));
    modal.innerHTML =
        `<h3>${country.name}</h3>` +
        `<a href="https://en.wikipedia.org/wiki/${wikiSlug}" target="_blank" rel="noopener noreferrer">` +
        `Wikipedia →</a>`;

    // Position so the cursor lands inside the modal (offset back by padding amount)
    const x = Math.max(0, Math.min(clientX - 20, window.innerWidth  - 210));
    const y = Math.max(0, Math.min(clientY - 20, window.innerHeight - 90));
    modal.style.left = `${x}px`;
    modal.style.top  = `${y}px`;
    modal.style.display = 'block';
}

function hideModal() {
    const modal = document.getElementById('country-modal');
    if (modal) modal.style.display = 'none';
}

/**
 * Attach projection hover/click handlers.
 * @returns {Function} cleanup — removes all listeners (call on view deactivate)
 */
export function initProjectionHover(app) {
    if (!document.getElementById('country-modal')) createModal();

    const getProjectionLocal = (clientX, clientY) => {
        const rect      = app.renderer.app.view.getBoundingClientRect();
        const container = app.renderer.projection.container;
        return {
            x: clientX - rect.left - container.x,
            y: clientY - rect.top  - container.y,
        };
    };

    const isOverProjection = (clientX, clientY) => {
        const rect      = app.renderer.app.view.getBoundingClientRect();
        const container = app.renderer.projection.container;
        const lx = clientX - rect.left - container.x;
        const ly = clientY - rect.top  - container.y;
        return lx >= 0 && lx <= app.renderer.projection.width
            && ly >= 0 && ly <= app.renderer.projection.height;
    };

    const onMouseMove = (event) => {
        if (isUIEvent(event)) return;

        if (!app.hitTestCache || !isOverProjection(event.clientX, event.clientY)) {
            if (app.hoveredCountryId !== null) {
                app.hoveredCountryId = null;
                document.body.style.cursor = 'default';
                emit('render:dirty');
            }
            return;
        }

        const { x, y } = getProjectionLocal(event.clientX, event.clientY);
        const found = findCountryAtPoint(x, y, app.hitTestCache);

        document.body.style.cursor = found ? 'pointer' : 'default';

        if (found !== app.hoveredCountryId) {
            app.hoveredCountryId = found;
            emit('render:dirty');
        }
    };

    const onClick = (event) => {
        if (isUIEvent(event)) return;

        if (!app.hitTestCache || !isOverProjection(event.clientX, event.clientY)) {
            hideModal();
            return;
        }

        const { x, y } = getProjectionLocal(event.clientX, event.clientY);
        const found = findCountryAtPoint(x, y, app.hitTestCache);

        if (found) {
            const country = app.countryData.countries.get(found);
            if (country) showModal(country, event.clientX, event.clientY);
        } else {
            hideModal();
        }
    };

    const onKeyDown = (event) => {
        if (event.key === 'Escape') hideModal();
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click',     onClick);
    window.addEventListener('keydown',   onKeyDown);

    return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('click',     onClick);
        window.removeEventListener('keydown',   onKeyDown);
        hideModal();
        app.hoveredCountryId = null;
    };
}
